<template>
  <div class="campaigns-container">
    <!-- Header Bar -->
    <div class="campaigns-header">
      <div class="header-left">
        <v-icon class="header-icon">mdi-speakerphone</v-icon>
        <span class="header-title">All Campaigns</span>
      </div>
      <v-btn
        class="add-btn"
        :to="{ name: 'AddCampaign' }"
        variant="flat"
        size="small"
      >
        <v-icon size="16" class="mr-1">mdi-plus</v-icon>
        Add Campaign
      </v-btn>
    </div>

    <!-- Filters Card -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <span class="filter-label">Start Date</span>
          <input
            class="filter-input"
            type="date"
            v-model="formattedStartDate"
          />
        </div>
        <div class="filter-group">
          <span class="filter-label">End Date</span>
          <input class="filter-input" type="date" v-model="formattedEndDate" />
        </div>
        <div class="filter-group filter-group--wide">
          <span class="filter-label">Campaign Subject</span>
          <div class="filter-input-wrap">
            <v-icon class="filter-icon" size="16">mdi-magnify</v-icon>
            <input
              class="filter-input filter-input--with-icon"
              type="text"
              v-model="formData.subject"
              placeholder="Search by subject…"
            />
          </div>
        </div>
        <div class="filter-actions">
          <v-btn
            class="btn-search"
            variant="flat"
            size="small"
            @click="submitFilters"
          >
            <v-icon size="15" class="mr-1">mdi-magnify</v-icon>Search
          </v-btn>
          <v-btn
            class="btn-clear"
            variant="flat"
            size="small"
            @click="clearFilters"
          >
            <v-icon size="15" class="mr-1">mdi-close</v-icon>Clear
          </v-btn>
        </div>
        <div class="filter-group filter-group--right">
          <span class="filter-label">Quick Search</span>
          <div class="filter-input-wrap">
            <v-icon class="filter-icon" size="16">mdi-magnify</v-icon>
            <input
              class="filter-input filter-input--with-icon"
              type="text"
              v-model="search"
              placeholder="Filter table…"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="table-card">
      <div class="table-wrap">
        <table class="campaigns-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Sent At</th>
              <th>Subject</th>
              <th>Email Status</th>
              <th>Delivery</th>
              <th>Actions</th>
              <!-- <th>Bounced</th> -->
              <!-- <th>Clicked</th> -->
              <!-- <th>Opened</th> -->
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredCampaigns.length === 0">
              <td colspan="10" class="empty-row">No campaigns found.</td>
            </tr>
            <tr v-for="item in filteredCampaigns" :key="item.id">
              <td class="td-date">
                {{ item.created_at ? formatTime(item.created_at) : "—" }}
              </td>
              <td class="td-date">
                {{ item.sent_at ? formatTime(item.sent_at) : "—" }}
              </td>
              <td class="td-subject">
                <span class="subject-text" :title="item.subject">{{
                  item.subject
                }}</span>
              </td>
              <td>
                <span
                  class="chip"
                  :class="item.is_active ? 'chip--active' : 'chip--inactive'"
                >
                  {{ item.is_active ? "active" : "inactive" }}
                </span>
              </td>
              <td>
                <span class="chip" :class="getStatusChipClass(item.status)">{{
                  item.status
                }}</span>
              </td>
              <!-- <td><span class="stat-num">{{ item.number_sent ?? '—' }}</span></td>
              <td>
                <span class="stat-num" :class="item.number_bounced > 0 ? 'stat-danger' : 'stat-zero'">
                  {{ item.number_bounced ?? '—' }}
                </span>
              </td>
              <td><span class="stat-num">{{ item.number_clicked ?? '—' }}</span></td>
              <td><span class="stat-num">{{ item.number_opened ?? '—' }}</span></td> -->
              <td>
                <div class="row-actions">
                  <button
                    class="action-btn action-btn--edit"
                    title="Edit"
                    @click="
                      $router.push({
                        name: 'AddCampaign',
                        params: { id: item.id },
                      })
                    "
                  >
                    <v-icon size="16">mdi-pencil</v-icon>
                  </button>
                  <button
                    class="action-btn action-btn--export"
                    title="Export to Excel"
                    @click="exportToExcel(item)"
                  >
                    <v-icon size="16">mdi-file-export</v-icon>
                  </button>
                  <button
                    class="action-btn action-btn--history"
                    title="Send History"
                    @click="openSendHistory(item)"
                  >
                    <v-icon size="16">mdi-history</v-icon>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Loading Dialog -->
    <v-dialog v-model="loaderState" max-width="420" persistent>
      <v-card class="loader-card">
        <div class="loader-body">
          <v-progress-circular
            indeterminate
            color="#6c5ecf"
            size="36"
            width="3"
          />
          <span class="loader-text">Please wait…</span>
        </div>
        <v-progress-linear color="#6c5ecf" indeterminate height="2" />
      </v-card>
    </v-dialog>

    <!-- Send History Dialog -->
    <v-dialog v-model="historyDialog" max-width="860">
      <v-card class="history-card">
        <div class="history-header">
          <div class="history-title">
            <v-icon size="18" color="#854F0B">mdi-history</v-icon>
            <span>Send History</span>
            <span class="history-campaign-name">{{
              selectedCampaign?.subject
            }}</span>
          </div>
          <button class="dialog-close" @click="historyDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <!-- Summary Stats -->
        <div class="history-meta">
          <div class="meta-stat">
            <span class="meta-label">Total Sends</span>
            <span class="meta-value">{{ sendHistory.length }}</span>
          </div>
          <div class="meta-stat">
            <span class="meta-label">Total Blasted</span>
            <span class="meta-value">{{
              historyTotals.blasted.toLocaleString()
            }}</span>
          </div>
          <div class="meta-stat">
            <span class="meta-label">Total Sent</span>
            <span class="meta-value meta-value--teal">{{
              historyTotals.sent.toLocaleString()
            }}</span>
          </div>
          <div class="meta-stat">
            <span class="meta-label">Total Bounced</span>
            <span
              class="meta-value"
              :class="historyTotals.bounced > 0 ? 'meta-value--red' : ''"
            >
              {{ historyTotals.bounced.toLocaleString() }}
            </span>
          </div>
          <div class="meta-stat">
            <span class="meta-label">Total Opened</span>
            <span class="meta-value meta-value--blue">{{
              historyTotals.opened.toLocaleString()
            }}</span>
          </div>
          <div class="meta-stat">
            <span class="meta-label">Total Clicked</span>
            <span class="meta-value meta-value--purple">{{
              historyTotals.clicked.toLocaleString()
            }}</span>
          </div>
        </div>

        <div class="history-table-wrap">
          <table class="history-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Sent At</th>
                <th>Status</th>
                <th>List</th>
                <th>Blasted</th>
                <th>Sent</th>
                <th>Bounced</th>
                <th>Clicked</th>
                <th>Opened</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="sendHistory.length === 0">
                <td colspan="9" class="empty-row">No send history found.</td>
              </tr>
              <tr v-for="h in sendHistory" :key="h.id">
                <td class="td-id">{{ h.id }}</td>
                <td class="td-date">
                  {{ h.sent_at ? formatTime(h.sent_at) : "—" }}
                </td>
                <td>
                  <span
                    class="chip chip--sm"
                    :class="getStatusChipClass(h.status)"
                    >{{ h.status }}</span
                  >
                </td>
                <td class="td-list">{{ h.list_name }}</td>
                <td>
                  <span class="num-chip num-chip--gray">{{
                    h.recipient_count ?? 0
                  }}</span>
                </td>
                <td>
                  <span class="num-chip num-chip--teal">{{
                    h.number_sent ?? 0
                  }}</span>
                </td>
                <td>
                  <span
                    class="num-chip"
                    :class="
                      h.number_bounced > 0 ? 'num-chip--red' : 'num-chip--gray'
                    "
                  >
                    {{ h.number_bounced ?? 0 }}
                  </span>
                </td>
                <td>
                  <span class="num-chip num-chip--purple">{{
                    h.number_clicked ?? 0
                  }}</span>
                </td>
                <td>
                  <span class="num-chip num-chip--blue">{{
                    h.number_opened ?? 0
                  }}</span>
                </td>
                <td>
                  <button
                    class="action-btn action-btn--edit"
                    title="View Emails"
                    @click="openHistoryEmails(h)"
                  >
                    <v-icon size="16">mdi-email-search</v-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </v-dialog>

    <!-- new dialog, alongside historyDialog -->
    <v-dialog v-model="emailsDialog" max-width="900">
      <v-card class="history-card">
        <div class="history-header">
          <div class="history-title">
            <v-icon size="18" color="#854F0B">mdi-email</v-icon>
            <span>Emails for Send #{{ selectedHistory?.id }}</span>
          </div>
          <button class="dialog-close" @click="emailsDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="history-table-wrap">
          <table class="history-table">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Sent</th>
                <th>Delivered</th>
                <th>Opened</th>
                <th>Clicked</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="historyEmailsLoading">
                <td colspan="5" class="empty-row">Loading…</td>
              </tr>
              <tr v-else-if="historyEmails.length === 0">
                <td colspan="5" class="empty-row">
                  No emails found for this send.
                </td>
              </tr>
              <tr v-for="e in historyEmails" :key="e.id">
                <td class="td-list">{{ e.to_email }}</td>
                <td>
                  <span
                    class="chip chip--sm"
                    :class="e.is_sent ? 'chip--sent' : 'chip--draft'"
                    >{{ e.is_sent ? "sent" : "not sent" }}</span
                  >
                </td>
                <td>
                  <span
                    class="chip chip--sm"
                    :class="e.is_delivered ? 'chip--sent' : 'chip--draft'"
                    >{{ e.is_delivered ? "delivered" : "undelivered" }}</span
                  >
                </td>
                <td>
                  <span
                    class="chip chip--sm"
                    :class="e.is_opened ? 'chip--sent' : 'chip--draft'"
                    >{{ e.is_opened ? "opened" : "unopened" }}</span
                  >
                </td>
                <td>
                  <span
                    class="chip chip--sm"
                    :class="e.is_clicked ? 'chip--sent' : 'chip--draft'"
                    >{{ e.is_clicked ? "clicked" : "not clicked" }}</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "AllCampaigns",

  beforeRouteEnter(to, from, next) {
    next((v) => {
      v.$store.dispatch("campaign/getCampaigns", v.formData);
    });
  },

  data() {
    return {
      selectedEndDate: null,
      selectedStartDate: null,
      formData: { start_date: null, end_date: null, subject: null },
      search: "",
      loadingState: false,
      oldValue: false,
      historyDialog: false,
      selectedCampaign: null,
      sendHistoryData: [],
      emailsDialog: false,
      selectedHistory: null,
      historyEmails: [],
      historyEmailsLoading: false,
    };
  },

  computed: {
    campaigns() {
      return this.$store.getters["campaign/campaignGetters"]("campaigns") || [];
    },
    loaderState() {
      return this.$store.getters["campaign/campaignGetters"]("loaderState");
    },
    sendHistory() {
      return (
        this.$store.getters["campaign/campaignGetters"]("campaignHistory") ||
        this.sendHistoryData
      );
    },
    filteredCampaigns() {
      if (!this.search) return this.campaigns;
      const q = this.search.toLowerCase();
      return this.campaigns.filter(
        (c) =>
          (c.subject || "").toLowerCase().includes(q) ||
          (c.status || "").toLowerCase().includes(q),
      );
    },
    historyTotals() {
      return this.sendHistory.reduce(
        (acc, h) => ({
          blasted: acc.blasted + (h.recipient_count || 0),
          sent: acc.sent + (h.number_sent || 0),
          bounced: acc.bounced + (h.number_bounced || 0),
          opened: acc.opened + (h.number_opened || 0),
          clicked: acc.clicked + (h.number_clicked || 0),
        }),
        { blasted: 0, sent: 0, bounced: 0, opened: 0, clicked: 0 },
      );
    },
    formattedStartDate: {
      get() {
        return this.selectedStartDate;
      },
      set(v) {
        this.selectedStartDate = this.formatDate(v);
      },
    },
    formattedEndDate: {
      get() {
        return this.selectedEndDate;
      },
      set(v) {
        this.selectedEndDate = this.formatDate(v);
      },
    },
  },

  methods: {
    getStatusChipClass(status) {
      const map = {
        sent: "chip--sent",
        draft: "chip--draft",
        sending: "chip--sending",
        scheduled: "chip--scheduled",
        queued: "chip--queued",
      };
      return map[status] || "chip--draft";
    },

    async openSendHistory(campaign) {
      this.selectedCampaign = campaign;
      this.sendHistoryData = [];
      this.historyDialog = true;
      try {
        const res = await this.$store.dispatch(
          "campaign/getCampaignHistory",
          campaign.id,
        );
        this.sendHistoryData = res?.data ?? [];
      } catch (e) {
        console.error("Failed to load history", e);
      }
    },

    exportToExcel(val) {
      const data = [
        {
          subject: val.subject,
          name: val.name,
          is_active: val.is_active ? "active" : "inactive",
          status: val.status,
          number_sent: val.number_sent,
          number_bounced: val.number_bounced,
          number_clicked: val.number_clicked,
          number_opened: val.number_opened,
        },
      ];
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Campaigns");
      XLSX.writeFile(wb, `${data[0].subject}.xlsx`);
    },

    formatTime(val) {
      return new Date(val).toISOString().replace("T", " ").split(".")[0];
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toISOString().split("T")[0];
    },

    submitFilters() {
      this.formData.start_date = this.formattedStartDate;
      this.formData.end_date = this.formattedEndDate;
      this.$store.dispatch("campaign/getCampaigns", this.formData);
    },

    clearFilters() {
      this.formattedStartDate = null;
      this.formattedEndDate = null;
      this.formData.subject = null;
      this.$store.dispatch("campaign/getCampaigns", this.formData);
    },

    async openHistoryEmails(history) {
      this.selectedHistory = history;
      this.historyEmails = [];
      this.emailsDialog = true;
      this.historyEmailsLoading = true;
      try {
        const res = await this.$store.dispatch(
          "campaign/getCampaignHistoryEmails",
          history.id,
        );
        this.historyEmails = res?.data ?? [];
      } catch (e) {
        console.error("Failed to load history emails", e);
      } finally {
        this.historyEmailsLoading = false;
      }
    },
  },

  watch: {
    loaderState(newValue, oldValue) {
      this.oldValue = oldValue;
      this.loadingState = newValue;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap");

/* ── Base ───────────────────────────────────────── */
.campaigns-container {
  font-family: "Poppins", sans-serif;
  background: #f4f4f8;
  min-height: 100vh;
  padding: 0 0 2rem;
  margin-left: -20px;
  margin-top: -10px;
}

/* ── Header ─────────────────────────────────────── */
.campaigns-header {
  background: #433876;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.header-icon {
  color: #c4b8f0 !important;
  font-size: 20px !important;
}
.header-title {
  color: #fff;
  font-size: 15px;
  font-weight: 500;
}
.add-btn {
  background: #6c5ecf !important;
  color: #fff !important;
  font-family: "Poppins", sans-serif;
  font-size: 12px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
}
.add-btn:hover {
  background: #7d6fd8 !important;
}

/* ── Filters ─────────────────────────────────────── */
.filters-card {
  background: #fff;
  border: 0.5px solid #e0dfe8;
  border-radius: 10px;
  padding: 16px 20px;
  margin: 0 16px 16px;
}
.filters-row {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.filter-group--wide {
  flex: 1;
  min-width: 200px;
}
.filter-group--right {
  margin-left: auto;
  min-width: 220px;
}
.filter-label {
  font-size: 10px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.filter-input {
  height: 36px;
  border: 0.5px solid #ccc;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  font-family: "Poppins", sans-serif;
  color: #333;
  background: #fafafa;
  outline: none;
  min-width: 140px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.filter-input:focus {
  border-color: #6c5ecf;
  box-shadow: 0 0 0 3px #6c5ecf18;
}
.filter-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.filter-icon {
  position: absolute;
  left: 10px;
  color: #aaa;
  pointer-events: none;
}
.filter-input--with-icon {
  padding-left: 32px;
  width: 100%;
}
.filter-actions {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding-bottom: 0;
}
.btn-search {
  background: #e24b4a !important;
  color: #fff !important;
  font-family: "Poppins", sans-serif !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
  height: 36px !important;
}
.btn-clear {
  background: #f0f0f4 !important;
  color: #666 !important;
  font-family: "Poppins", sans-serif !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

/* ── Table Card ─────────────────────────────────── */
.table-card {
  background: #fff;
  border: 0.5px solid #e0dfe8;
  border-radius: 10px;
  margin: 0 16px;
  overflow: hidden;
}
.table-wrap {
  overflow-x: auto;
}
.campaigns-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.campaigns-table thead tr {
  background: #f7f6fc;
  border-bottom: 0.5px solid #e0dfe8;
}
.campaigns-table thead th {
  padding: 10px 14px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.campaigns-table tbody tr {
  border-bottom: 0.5px solid #f0eff6;
  transition: background 0.12s;
}
.campaigns-table tbody tr:last-child {
  border-bottom: none;
}
.campaigns-table tbody tr:hover {
  background: #f9f8fd;
}
.campaigns-table td {
  padding: 11px 14px;
  vertical-align: middle;
}

/* ── Table cell types ───────────────────────────── */
.td-date {
  color: #888;
  font-size: 11px;
  white-space: nowrap;
}
.td-subject {
  max-width: 240px;
}
.subject-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: #333;
  max-width: 240px;
}
.stat-num {
  font-weight: 500;
  color: #333;
}
.stat-zero {
  color: #bbb;
}
.stat-danger {
  color: #a32d2d;
  font-weight: 600;
}
.empty-row {
  text-align: center;
  color: #aaa;
  padding: 32px !important;
  font-size: 13px;
}

/* ── Status chips ───────────────────────────────── */
.chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.chip--sm {
  font-size: 10px;
  padding: 2px 8px;
}
.chip--active {
  background: #e1f5ee;
  color: #0f6e56;
}
.chip--inactive {
  background: #faeeda;
  color: #854f0b;
}
.chip--sent {
  background: #e1f5ee;
  color: #0f6e56;
}
.chip--draft {
  background: #faeeda;
  color: #854f0b;
}
.chip--sending {
  background: #e6f1fb;
  color: #185fa5;
}
.chip--scheduled {
  background: #eeedfe;
  color: #534ab7;
}
.chip--queued {
  background: #eeedfe;
  color: #534ab7;
}

/* ── Row action buttons ─────────────────────────── */
.row-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}
.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  background: transparent;
  color: #aaa;
  transition: background 0.15s, color 0.15s;
}
.action-btn--edit:hover {
  background: #e6f1fb;
  color: #185fa5;
}
.action-btn--export:hover {
  background: #eaf3de;
  color: #3b6d11;
}
.action-btn--history:hover {
  background: #faeeda;
  color: #854f0b;
}

/* ── Loader ─────────────────────────────────────── */
.loader-card {
  border-radius: 10px !important;
  overflow: hidden;
}
.loader-body {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px 20px 20px;
}
.loader-text {
  font-size: 13px;
  color: #555;
  font-family: "Poppins", sans-serif;
}

/* ── History Dialog ─────────────────────────────── */
.history-card {
  border-radius: 12px !important;
  overflow: hidden;
}
.history-header {
  background: #433876;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.history-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
}
.history-campaign-name {
  color: #c4b8f0;
  font-size: 12px;
  font-weight: 400;
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dialog-close {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  cursor: pointer;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.dialog-close:hover {
  background: rgba(255, 255, 255, 0.22);
}

/* Summary stats row */
.history-meta {
  display: flex;
  gap: 0;
  border-bottom: 0.5px solid #e0dfe8;
  background: #f7f6fc;
}
.meta-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 20px;
  border-right: 0.5px solid #e0dfe8;
  flex: 1;
}
.meta-stat:last-child {
  border-right: none;
}
.meta-label {
  font-size: 9px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.meta-value {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}
.meta-value--teal {
  color: #0f6e56;
}
.meta-value--red {
  color: #a32d2d;
}
.meta-value--blue {
  color: #185fa5;
}
.meta-value--purple {
  color: #534ab7;
}

/* History table */
.history-table-wrap {
  overflow-x: auto;
  max-height: 420px;
  overflow-y: auto;
}
.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.history-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}
.history-table thead tr {
  background: #f7f6fc;
  border-bottom: 0.5px solid #e0dfe8;
}
.history-table thead th {
  padding: 9px 12px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.history-table tbody tr {
  border-bottom: 0.5px solid #f0eff6;
  transition: background 0.12s;
}
.history-table tbody tr:last-child {
  border-bottom: none;
}
.history-table tbody tr:hover {
  background: #f9f8fd;
}
.history-table td {
  padding: 10px 12px;
  vertical-align: middle;
}
.td-id {
  color: #bbb;
  font-size: 11px;
}
.td-list {
  font-weight: 500;
  color: #333;
}

/* Number chips for history */
.num-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
}
.num-chip--gray {
  background: #f0f0f4;
  color: #666;
}
.num-chip--teal {
  background: #e1f5ee;
  color: #0f6e56;
}
.num-chip--red {
  background: #fcebeb;
  color: #a32d2d;
}
.num-chip--blue {
  background: #e6f1fb;
  color: #185fa5;
}
.num-chip--purple {
  background: #eeedfe;
  color: #534ab7;
}
</style>
